import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Package } from "lucide-react";
import { PageHeader, PageBody, StatusPill, statusTone } from "@/components/PageChrome";
import { Button } from "@/components/controls";
import ImageThumb from "@/components/ImageThumb";
import { T } from "@/lib/testIds";

export default function WarehouseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [warehouse, setWarehouse] = useState(null);
  const [inventory, setInventory] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data: w } = await api.get(`/warehouse/${id}`);
        setWarehouse(w);
        const { data: inv } = await api.get(`/inventory/warehouse/${encodeURIComponent(w.name)}`);
        setInventory(inv);
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/warehouse");
      }
    })();
  }, [id, navigate]);

  if (!warehouse) return <div className="p-6 text-slate-500">Loading…</div>;

  const totalQty = inventory.reduce((s, r) => s + Number(r.qty || 0), 0);
  const totalTransit = inventory.reduce((s, r) => s + Number(r.in_transit_qty || 0) + Number(r.in_transit_allocated || 0), 0);
  const totalPlaced = inventory.reduce((s, r) => s + Number(r.qty_placed || 0), 0);

  return (
    <>
      <PageHeader title={warehouse.name} subtitle={warehouse.location || "—"} testId="warehouse-detail-title">
        <Button onClick={() => navigate("/warehouse")}><ArrowLeft size={13} /> Back</Button>
      </PageHeader>
      <PageBody>
        <div className="grid grid-cols-4 gap-3 mb-4">
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="field-label">SKUs</div>
            <div className="font-mono text-2xl font-semibold">{inventory.length}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="field-label">Total Qty in Stock</div>
            <div className="font-mono text-2xl font-semibold">{totalQty.toLocaleString()}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="field-label">In-Transit (Alloc + Unalloc)</div>
            <div className="font-mono text-2xl font-semibold">{totalTransit.toLocaleString()}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-4">
            <div className="field-label">Qty Placed (China: Added)</div>
            <div className="font-mono text-2xl font-semibold">{totalPlaced.toLocaleString()}</div>
          </div>
        </div>

        <div className="border border-slate-200 bg-white rounded-sm overflow-hidden">
          <div className="px-4 py-2 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
            <Package size={14} className="text-slate-500" />
            <span className="font-heading text-[13px] font-semibold">Inventory in this warehouse</span>
          </div>
          <table className="erp-table">
            <thead>
              <tr>
                <th>Image</th>
                <th>System SKU</th>
                <th>Product</th>
                <th>Qty</th>
                <th>In-Transit (Unalloc.)</th>
                <th>In-Transit (Alloc.)</th>
                <th>Qty Placed</th>
                <th>Bin</th>
              </tr>
            </thead>
            <tbody>
              {inventory.map((r) => (
                <tr key={r.id} onClick={() => navigate(`/inventory?sku=${r.system_sku}`)} className="cursor-pointer">
                  <td>{r.image_url ? <ImageThumb src={r.image_url} size={64} testId={`wh-thumb-${r.id}`} /> : "—"}</td>
                  <td className="font-mono">{r.system_sku}</td>
                  <td>{r.product_name}</td>
                  <td className="font-mono">{r.qty ?? 0}</td>
                  <td className="font-mono">{r.in_transit_qty ?? 0}</td>
                  <td className="font-mono">{r.in_transit_allocated || 0}</td>
                  <td className="font-mono">{r.qty_placed || 0}</td>
                  <td className="font-mono">{r.bin_id || "—"}</td>
                </tr>
              ))}
              {!inventory.length && (
                <tr><td colSpan={8} className="text-center py-8 text-slate-500">No inventory in this warehouse.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </PageBody>
    </>
  );
}
